import {
  Docket,
  DocketItem,
  DocketItemStatus,
  DocketStatus,
  NotifierDisplayStyle,
  PrinterProfile,
} from '@oolio-group/domain';
import { translate } from '@oolio-group/localization';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayNotifierHeaderItem,
  DocketDisplayNotifierItem,
  DocketDisplayNotifierProfileItem,
  DocketDisplayNotifierSummaryItem,
} from '../../common/types';
import { DocketDisplayItemTypeHeight } from '../../common/constants';
import { hasAllergens } from '../allergens';
import { getDocketItemDisplayName } from './docketItemDisplayName';
import { Session } from '../../state/preferences';
import {
  getAllergenLabelsHeight,
  getItemNameHeightAdjustment,
  getNotesHeight,
} from './transformDockets';

export type NotifierItemTextStyleKey =
  | 'preppedItem'
  | 'completedItem'
  | 'servedItem'
  | 'voidedItem'
  | 'transferredItem';

export const isNotifierItemPending = (item: DocketItem): boolean =>
  (item.itemStatus === DocketItemStatus.CREATED ||
    item.itemStatus === DocketItemStatus.RECALLED) &&
  item.status !== DocketStatus.COMPLETED;

export const getNotifierItemTextStyleKey = (
  itemStatus: DocketItemStatus,
  docketStatus: DocketStatus,
): NotifierItemTextStyleKey | undefined => {
  if (itemStatus === DocketItemStatus.TRANSFERRED) return 'transferredItem';
  if (itemStatus === DocketItemStatus.VOIDED) return 'voidedItem';
  if (docketStatus === DocketStatus.COMPLETED) return 'servedItem';
  if (docketStatus !== DocketStatus.CREATED) return undefined;
  switch (itemStatus) {
    case DocketItemStatus.BUMPED:
      return 'preppedItem';
    case DocketItemStatus.REBUMPED:
      return 'completedItem';
    case DocketItemStatus.SERVED:
      return 'servedItem';
    default:
      return undefined;
  }
};

const sumQuantity = (items: DocketItem[]): number =>
  items.reduce((total, item) => total + (item.quantity || 0), 0);

const getNotifierItemHeight = (item: DocketItem): number => {
  const nameAdjustment = getItemNameHeightAdjustment(
    getDocketItemDisplayName(item),
  );
  const notesHeight = item.notes ? getNotesHeight(item.notes) : 0;
  const modifiersHeight =
    (item.modifiers?.length || 0) *
    DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'MODIFIER'];

  const allergensHeight = hasAllergens(item.allergens)
    ? getAllergenLabelsHeight(item)
    : 0;

  return (
    DocketDisplayItemTypeHeight[DocketDisplayItemType.NOTIFIER_ITEM] +
    nameAdjustment +
    notesHeight +
    modifiersHeight +
    allergensHeight
  );
};

export const constructNotifierRows = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session: Session,
  collapsedByDocketId: Record<string, boolean>,
): DocketDisplayItem[] => {
  const groups = (docket.notifierItems || []).filter(
    group => group.items?.length,
  );
  if (!groups.length) return [];

  const isCollapsed = !!collapsedByDocketId[docket.id];
  const header: DocketDisplayNotifierHeaderItem = {
    type: DocketDisplayItemType.NOTIFIER_HEADER,
    name: translate('kitchenDisplay.otherItems'),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.NOTIFIER_HEADER],
    docketId: docket.id,
    status: docket.status,
    totalQuantity: groups.reduce(
      (total, group) => total + sumQuantity(group.items),
      0,
    ),
    isCollapsed,
  };
  if (isCollapsed) return [header];

  const displayStyle = assignedProfiles.find(
    profile => profile.id === docket.printerProfileId,
  )?.notifiers?.displayStyle;
  const isFullContent = displayStyle === NotifierDisplayStyle.SHOW_FULL_CONTENT;
  const profileNames = new Map(
    (session?.allPrinterProfiles || []).map(profile => [
      profile.id,
      profile.name,
    ]),
  );

  const rows: DocketDisplayItem[] = [header];
  groups.forEach(group => {
    const profileName = profileNames.get(group.printerProfileId) || '';

    if (!isFullContent) {
      const summary: DocketDisplayNotifierSummaryItem = {
        type: DocketDisplayItemType.NOTIFIER_SUMMARY,
        name: profileName,
        height:
          DocketDisplayItemTypeHeight[DocketDisplayItemType.NOTIFIER_SUMMARY],
        docketId: docket.id,
        quantity: sumQuantity(group.items),
        itemCount: group.items.length,
        bumpedCount: group.items.filter(item => !isNotifierItemPending(item))
          .length,
      };
      rows.push(summary);
      return;
    }

    const profileRow: DocketDisplayNotifierProfileItem = {
      type: DocketDisplayItemType.NOTIFIER_PROFILE,
      name: profileName,
      height:
        DocketDisplayItemTypeHeight[DocketDisplayItemType.NOTIFIER_PROFILE],
      docketId: docket.id,
    };
    rows.push(profileRow);
    group.items.forEach(item => {
      const itemRow: DocketDisplayNotifierItem = {
        type: DocketDisplayItemType.NOTIFIER_ITEM,
        name: getDocketItemDisplayName(item),
        height: getNotifierItemHeight(item),
        docketId: docket.id,
        item,
      };
      rows.push(itemRow);
    });
  });

  return rows;
};
